import { forwardRef } from 'react'
import { resolveComponentVariant } from '../componentContract'
import styles from './Primitives.module.css'

const variants = ['primary', 'secondary', 'ghost', 'danger']
const sizeAliases = { sm: 'compact', md: 'default', lg: 'large', xl: 'large' }
const sizes = ['compact', 'default', 'large']

function resolveSize(size) {
  const normalized = sizeAliases[size] || size
  return resolveComponentVariant('Button size', normalized, sizes, 'default')
}

export const Button = forwardRef(function Button({
  variant = 'primary',
  size = 'default',
  loading = false,
  children,
  className = '',
  disabled = false,
  type = 'button',
  ...rest
}, ref) {
  const resolvedVariant = resolveComponentVariant('Button variant', variant, variants, 'primary')
  const resolvedSize = resolveSize(size)
  const classes = [
    styles.button,
    styles[resolvedVariant],
    resolvedSize !== 'default' ? styles[resolvedSize] : '',
    className,
  ].filter(Boolean).join(' ')

  return (
    <button
      {...rest}
      ref={ref}
      className={classes}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
    >
      {loading ? <span className={styles.spinner} aria-hidden="true" /> : null}
      {loading ? <span className={styles.loadingContent}>{children}</span> : children}
    </button>
  )
})

export const LinkButton = forwardRef(function LinkButton({
  variant = 'primary',
  size = 'default',
  disabled = false,
  children,
  className = '',
  onClick,
  ...rest
}, ref) {
  const resolvedVariant = resolveComponentVariant('LinkButton variant', variant, variants, 'primary')
  const resolvedSize = resolveSize(size)
  const classes = [
    styles.button,
    styles[resolvedVariant],
    resolvedSize !== 'default' ? styles[resolvedSize] : '',
    disabled ? styles.disabledLink : '',
    className,
  ].filter(Boolean).join(' ')

  return (
    <a
      {...rest}
      ref={ref}
      className={classes}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : rest.tabIndex}
      onClick={(event) => {
        if (disabled) {
          event.preventDefault()
          return
        }
        onClick?.(event)
      }}
    >
      {children}
    </a>
  )
})
